import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Loader2, Building2 } from 'lucide-react';

const initialFormData = {
  name: '',
  segment: 'Advice',
  relationshipLead: '',
  restricted: false,
  netWorth: ''
};

export default function AddClientModal({ isOpen, onClose, onSave }) {
  const [formData, setFormData] = useState(initialFormData);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});

  const validateForm = () => {
    const newErrors = {};
    if (!formData.name.trim()) {
      newErrors.name = 'Client name is required';
    }
    if (!formData.relationshipLead.trim()) {
      newErrors.relationshipLead = 'Relationship lead is required';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) return;

    setSaving(true);
    try {
      const clientData = {
        name: formData.name.trim(),
        segment: formData.segment,
        relationshipLead: formData.relationshipLead.trim(),
        restricted: formData.restricted,
        netWorth: formData.netWorth ? parseFloat(formData.netWorth) : 0,
        stats: {
          entities: 0,
          individuals: 0,
          linksVerified: 0,
          tasksDue: 0
        },
        dataCompleteness: {
          individuals: 0,
          entities: 0,
          links: 0,
          documents: 0
        }
      };
      
      await onSave(clientData);
      setFormData(initialFormData);
      setErrors({});
      onClose();
    } catch (error) {
      console.error('Failed to save client:', error);
      setErrors({ submit: 'Failed to save client. Please try again.' });
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    setFormData(initialFormData);
    setErrors({});
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[500px] bg-white" data-testid="add-client-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-heading text-xl text-[#2C2420]">
            <Building2 className="w-5 h-5 text-[#4A3B32]" />
            Add New Client
          </DialogTitle>
          <DialogDescription className="text-[#5D524D]">
            Create a new client group for your portfolio.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Client Name */}
          <div className="space-y-2">
            <Label htmlFor="name" className="text-[#2C2420]">
              Client Name <span className="text-red-500">*</span>
            </Label>
            <Input
              id="name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="e.g., Smith Family Group"
              className={`caffeine-input ${errors.name ? 'border-red-500' : ''}`}
              data-testid="client-name-input"
            />
            {errors.name && (
              <p className="text-sm text-red-500">{errors.name}</p>
            )}
          </div>

          {/* Segment */}
          <div className="space-y-2">
            <Label className="text-[#2C2420]">Segment</Label>
            <Select
              value={formData.segment}
              onValueChange={(value) => setFormData({ ...formData, segment: value })}
            >
              <SelectTrigger className="caffeine-input" data-testid="client-segment-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Advice">Advice</SelectItem>
                <SelectItem value="Family Office">Family Office</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Relationship Lead */}
          <div className="space-y-2">
            <Label htmlFor="relationshipLead" className="text-[#2C2420]">
              Relationship Lead <span className="text-red-500">*</span>
            </Label>
            <Input
              id="relationshipLead"
              value={formData.relationshipLead}
              onChange={(e) => setFormData({ ...formData, relationshipLead: e.target.value })}
              placeholder="e.g., Sarah Mitchell"
              className={`caffeine-input ${errors.relationshipLead ? 'border-red-500' : ''}`}
              data-testid="client-lead-input"
            />
            {errors.relationshipLead && (
              <p className="text-sm text-red-500">{errors.relationshipLead}</p>
            )}
          </div>

          {/* Net Worth */}
          <div className="space-y-2">
            <Label htmlFor="netWorth" className="text-[#2C2420]">
              Net Worth (Indicative)
            </Label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8C8078]">$</span>
              <Input
                id="netWorth"
                type="number"
                value={formData.netWorth}
                onChange={(e) => setFormData({ ...formData, netWorth: e.target.value })}
                placeholder="0"
                className="caffeine-input pl-7"
                data-testid="client-networth-input"
              />
            </div>
            <p className="text-xs text-[#8C8078]">Optional. Enter value in dollars.</p>
          </div>

          {/* Restricted Toggle */}
          <div className="flex items-center justify-between py-2">
            <div>
              <Label className="text-[#2C2420]">Restricted Access</Label>
              <p className="text-xs text-[#8C8078]">Limit access to authorized users only</p>
            </div>
            <Switch
              checked={formData.restricted}
              onCheckedChange={(checked) => setFormData({ ...formData, restricted: checked })}
              data-testid="client-restricted-toggle"
            />
          </div>

          {errors.submit && (
            <p className="text-sm text-red-500 text-center">{errors.submit}</p>
          )}
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={handleClose}
            className="caffeine-btn-ghost"
            data-testid="cancel-add-client-btn"
          >
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={saving}
            className="caffeine-btn"
            data-testid="save-client-btn"
          >
            {saving ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Saving...
              </>
            ) : (
              'Create Client'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
